import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { UserRole } from '@/mocks/shared';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  label: string;
  icon: string;
  path: string;
  divider?: boolean;
}

const navByRole: Record<UserRole, NavItem[]> = {
  admin: [
    { label: 'Dashboard', icon: 'ri-dashboard-line', path: '/admin' },
    { label: 'Produits', icon: 'ri-store-line', path: '/admin/products' },
    { label: 'Événements', icon: 'ri-calendar-event-line', path: '/admin/events' },
    { label: 'Salles', icon: 'ri-community-line', path: '/admin/lieux' },
    { label: 'Églises', icon: 'ri-building-4-line', path: '/admin/eglises' },
    { label: 'Finance Produits', icon: 'ri-money-dollar-circle-line', path: '/admin/finance-products' },
    { label: 'Finance Événements', icon: 'ri-ticket-2-line', path: '/admin/finance-events' },
    { label: 'Finance Salles', icon: 'ri-building-2-line', path: '/admin/finance-lieux' },
    { label: 'Paiements Messes', icon: 'ri-church-line', path: '/admin/messe-payments' },
    { label: 'Utilisateurs', icon: 'ri-team-line', path: '/admin/users' },
    { label: 'Journaux', icon: 'ri-file-list-3-line', path: '/admin/logs' },
    { label: 'Contenu app', icon: 'ri-edit-box-line', path: '/admin/app-content' },
    { label: 'Profil', icon: 'ri-user-settings-line', path: '/admin/profile', divider: true },
  ],
  vendor: [
    { label: 'Dashboard', icon: 'ri-dashboard-line', path: '/vendor' },
    { label: 'Mes Produits', icon: 'ri-store-line', path: '/vendor/products' },
    { label: 'Commandes', icon: 'ri-shopping-bag-line', path: '/vendor/orders' },
    { label: 'Notifications', icon: 'ri-notification-line', path: '/vendor/notifications' },
    { label: 'Mon Profil', icon: 'ri-user-settings-line', path: '/vendor/profile', divider: true },
  ],
  organizer: [
    { label: 'Dashboard', icon: 'ri-dashboard-line', path: '/organizer' },
    { label: 'Mes Événements', icon: 'ri-calendar-event-line', path: '/organizer/events' },
    { label: 'Tickets', icon: 'ri-ticket-line', path: '/organizer/tickets' },
    { label: 'Finances', icon: 'ri-line-chart-line', path: '/organizer/finances' },
    { label: 'Notifications', icon: 'ri-notification-line', path: '/organizer/notifications' },
    { label: 'Mon Profil', icon: 'ri-user-settings-line', path: '/organizer/profile', divider: true },
  ],
};

const roleMeta: Record<UserRole, { label: string; icon: string; color: string }> = {
  admin: { label: 'Administrateur', icon: 'ri-shield-user-fill', color: 'text-gold' },
  vendor: { label: 'Vendeur', icon: 'ri-store-2-fill', color: 'text-emerald-400' },
  organizer: { label: 'Organisateur', icon: 'ri-calendar-event-fill', color: 'text-gold-light' },
};

interface Props {
  role: UserRole;
  userName: string;
  userAvatar: string;
  badgeCounts?: Partial<Record<string, number>>; // path -> unread count
}

export default function DashboardSidebar({ role, userName, userAvatar, badgeCounts = {} }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { logout } = useAuth();

  const navItems = navByRole[role];
  const meta = roleMeta[role];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside
      className={`flex h-auto w-full flex-shrink-0 flex-col bg-navy transition-all duration-300 md:h-screen ${collapsed ? 'md:w-16' : 'md:w-60'}`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 border-b border-navy-light px-4 py-3 md:py-5 ${collapsed ? 'md:justify-center' : ''}`}>
        <img
          src="https://static.readdy.ai/image/7e0624576d141773c913eba1868f652c/8b980f2d5169abf278896e2c5cd235e5.jpeg"
          alt="Logo"
          className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
        />
        <div className={`overflow-hidden ${collapsed ? 'md:hidden' : ''}`}>
            <p className="text-white text-sm font-bold leading-tight">MarketPlace</p>
            <p className="text-navy-muted text-xs">& Events</p>
        </div>
      </div>

      {/* Role Badge */}
      <div className={`mx-3 mt-3 hidden rounded-xl bg-navy-light p-3 md:block ${collapsed ? 'md:hidden' : ''}`}>
          <div className="flex items-center gap-2">
            {userAvatar ? (
              <div className="w-9 h-9 flex items-center justify-center rounded-full overflow-hidden">
                <img src={userAvatar} alt={userName} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold text-navy text-xs font-bold">
                {userName.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="overflow-hidden">
              <p className="text-white text-xs font-semibold truncate">{userName}</p>
              <div className="flex items-center gap-1">
                <i className={`${meta.icon} ${meta.color} text-xs`}></i>
              <p className="text-navy-muted text-xs truncate">{meta.label}</p>
              </div>
            </div>
          </div>
      </div>

      {/* Navigation */}
      <nav className="flex gap-2 overflow-x-auto px-2 py-3 md:block md:flex-1 md:space-y-1 md:overflow-y-auto md:py-4">
        {navItems.map((item) => {
          const isActive = item.path === '/admin'
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path);
          return (
            <div key={item.path}>
              {item.divider && (
                <div className="mx-3 my-2 hidden border-t border-navy-light opacity-50 md:block" />
              )}
              <button
                onClick={() => navigate(item.path)}
                className={`flex w-full min-w-max items-center gap-2.5 rounded-xl px-3 py-2.5 transition-all duration-150 cursor-pointer whitespace-nowrap md:gap-3 md:min-w-0
                  ${isActive
                    ? 'bg-gold text-white'
                    : 'text-navy-muted hover:bg-navy-light hover:text-white'
                  } ${collapsed ? 'md:justify-center' : ''}`}
              >
                <div className="relative w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-base`}></i>
                  {(badgeCounts[item.path] ?? 0) > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-0.5 leading-none">
                      {(badgeCounts[item.path] ?? 0) > 99 ? '99+' : badgeCounts[item.path]}
                    </span>
                  )}
                </div>
                <span className={`text-sm font-medium ${collapsed ? 'md:hidden' : ''}`}>{item.label}</span>
                {(badgeCounts[item.path] ?? 0) > 0 && (
                  <span className={`ml-auto hidden min-w-[20px] h-5 items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1 md:flex ${collapsed ? 'md:hidden' : ''}`}>
                    {(badgeCounts[item.path] ?? 0) > 99 ? '99+' : badgeCounts[item.path]}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </nav>

      {/* Collapse + Logout */}
      <div className="hidden space-y-1 border-t border-navy-light p-2 md:block">
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-navy-muted hover:bg-navy-light hover:text-white transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className={collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'}></i>
          </div>
          {!collapsed && <span className="text-sm">Réduire</span>}
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-navy-muted hover:bg-red-900/30 hover:text-red-400 transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-logout-box-line"></i>
          </div>
          {!collapsed && <span className="text-sm">Déconnexion</span>}
        </button>
      </div>
    </aside>
  );
}
